<script setup lang="ts">
/**
 * The first screen, and the one an owner opens every morning.
 *
 * Today's takings, what is still sitting on tables, the VAT split, the payment
 * mix, and whether the sales actually reached the tax authority. Every figure
 * is folded from the same event log the till wrote to — there is no separate
 * "reporting" copy that can drift.
 */

import { computed } from "vue";
import StatTile from "./StatTile.vue";
import type { CheckSummary, FiscalState, SalesReport } from "../api";
import { openChecks, tenderShares } from "../load";
import { money, rate, share as sharePercent } from "../format";

const props = defineProps<{
  sales: SalesReport;
  checks: readonly CheckSummary[];
  fiscal: FiscalState;
  /**
   * Whether the venue has an EET identity (DIČ poplatníka) at all. Without one
   * there is nothing to file yet: the tile says so, rather than a tick that
   * reads as "filed" or a red alarm about a certificate nobody needs until
   * the venue is registered. Absent means configured.
   */
  fiscalConfigured?: boolean;
  locale: string;
  t: (key: string) => string;
}>();

const day = computed(() => props.sales.totals);
const open = computed(() => openChecks(props.checks));
const shares = computed(() => tenderShares([...day.value.tenders]));

const quiet = computed(() => day.value.checkCount === 0 && open.value.count === 0);

/**
 * The tax authority's state, as one sentence.
 *
 * A PLAYGROUND ACKNOWLEDGEMENT IS NOT A SUCCESS. It is the one failure that
 * looks exactly like one, so it gets its own tone and its own words rather than
 * being folded into a green tick.
 */
const notSetUp = computed(
  () =>
    props.fiscalConfigured === false &&
    props.fiscal.playground.length === 0 &&
    props.fiscal.unreportable.length === 0 &&
    props.fiscal.quarantined.length === 0,
);

const fiscalTone = computed<"plain" | "good" | "warn" | "bad">(() => {
  if (notSetUp.value) return "plain";
  if (props.fiscal.credentials === "absent") return "bad";
  if (props.fiscal.playground.length > 0 || props.fiscal.unreportable.length > 0) return "warn";
  if (props.fiscal.quarantined.length > 0) return "bad";
  return "good";
});

const fiscalValue = computed(() => {
  if (notSetUp.value) return props.t("fiscal.notSetUp");
  if (props.fiscal.credentials === "absent") return props.t("fiscal.absent");
  if (props.fiscal.playground.length > 0) return String(props.fiscal.playground.length);
  if (props.fiscal.unreportable.length > 0) return String(props.fiscal.unreportable.length);
  return "✓";
});

const fiscalLabel = computed(() => {
  if (notSetUp.value) return props.t("fiscal.eet");
  if (props.fiscal.credentials === "absent") return props.t("fiscal.credentials");
  if (props.fiscal.playground.length > 0) return props.t("fiscal.playground");
  if (props.fiscal.unreportable.length > 0) return props.t("fiscal.unreportable");
  return props.t("fiscal.heading");
});
</script>

<template>
  <section>
    <div class="gf-tiles">
      <StatTile :label="t('today.takings')" :value="money(day.dueMinor, locale)" />
      <StatTile :label="t('today.checks')" :value="String(day.checkCount)" />
      <StatTile
        :label="t('today.average')"
        :value="money(day.averageCheckMinor, locale)"
      />
      <StatTile
        v-if="day.covers > 0"
        :label="t('sales.perCover')"
        :value="money(day.perCoverMinor, locale)"
        :hint="t('sales.coversHint').replace('{n}', String(day.covers))"
        data-testid="per-cover"
      />
      <StatTile
        :label="t('today.open')"
        :value="String(open.count)"
        :hint="open.count > 0 ? `${t('today.openMoney')}: ${money(open.onTablesMinor, locale)}` : undefined"
      />
      <StatTile
        :label="fiscalLabel"
        :value="fiscalValue"
        :tone="fiscalTone"
        :hint="notSetUp ? t('fiscal.notSetUpWhy') : fiscal.playground.length > 0 ? t('fiscal.playgroundWhy') : undefined"
      />
    </div>

    <p v-if="quiet" class="gf-quiet">
      {{ t("today.quiet") }}
      <span class="gf-quiet__hint">{{ t("today.quietHint") }}</span>
    </p>

    <div v-else class="gf-columns">
      <div class="gf-panel">
        <h2 class="gf-panel__title">{{ t("today.vat") }}</h2>
        <table class="gf-table">
          <thead>
            <tr>
              <th scope="col">{{ t("menu.rate") }}</th>
              <th scope="col" class="gf-num">{{ t("sales.revenue") }}</th>
              <th scope="col" class="gf-num">{{ t("sales.vat") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="line in day.taxLines" :key="line.rateBp">
              <th scope="row">{{ rate(line.rateBp, locale) }}</th>
              <td class="gf-num">{{ money(line.grossMinor, locale) }}</td>
              <td class="gf-num">{{ money(line.vatMinor, locale) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="gf-panel">
        <h2 class="gf-panel__title">{{ t("today.payments") }}</h2>
        <table class="gf-table">
          <thead>
            <tr>
              <th scope="col">{{ t("checks.tenders") }}</th>
              <th scope="col" class="gf-num">{{ t("common.total") }}</th>
              <th scope="col" class="gf-num">%</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="share in shares" :key="share.kind">
              <th scope="row">{{ t(`tender.${share.kind}`) }}</th>
              <td class="gf-num">{{ money(share.amountMinor, locale) }}</td>
              <td class="gf-num">{{ sharePercent(share.shareBp, locale) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="day.tipMinor > 0n" class="gf-note">
          {{ t("sales.tips") }}: {{ money(day.tipMinor, locale) }} —
          {{ t("sales.tipsNotRevenue") }}
        </p>
      </div>
    </div>
  </section>
</template>
